import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { AdminApiService } from '../admin-api.service';
import { SessionDto, UserListItem, PagedResult, UserSession } from '../models';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  standalone: true,
  selector: 'app-sessions-admin-page',
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    TooltipModule,
    DatePipe,
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['./sessions-admin.page.scss'],
  templateUrl: './sessions-admin.page.html',
})
export class SessionsAdminPage implements OnInit {
  api = inject(AdminApiService);
  ms = inject(MessageService);
  confirmService = inject(ConfirmationService);

  userSearch = '';
  candidateUsers: UserListItem[] = [];
  selectedUserId = '';
  selectedUser: UserListItem | null = null;
  rows: SessionDto[] = [];
  loading = false;
  searching = false;

  // Computed properties for template
  get activeSessionsCount(): number {
    return this.rows.filter(r => !r.isRevoked && !this.isExpired(r)).length;
  }

  get inactiveSessionsCount(): number {
    return this.rows.filter(r => r.isRevoked || this.isExpired(r)).length;
  }

  get hasActiveSessions(): boolean {
    return this.activeSessionsCount > 0;
  }

  ngOnInit() {
    // Auto-load if needed
  }

  findUsers() {
    if (!this.userSearch.trim()) {
      this.candidateUsers = [];
      return;
    }
    this.searching = true;
    this.api.listUsers({ search: this.userSearch.trim(), page: 1, pageSize: 50 }).subscribe({
      next: (result: PagedResult<UserListItem>) => {
        this.candidateUsers = result.items || [];
        this.searching = false;
      },
      error: (err) => {
        console.error('Error searching users:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل البحث عن المستخدمين',
        });
        this.searching = false;
      },
    });
  }

  pickUser(u: UserListItem) {
    this.selectedUserId = u.id;
    this.selectedUser = u;
    this.candidateUsers = [];
    this.userSearch = u.username;
    this.load();
  }

  load() {
    if (!this.selectedUserId) return;
    this.loading = true;
    // Use the admin endpoint that returns all sessions (including revoked)
    this.api.listUserSessionsOld(this.selectedUserId).subscribe({
      next: (sessions) => {
        this.rows = sessions || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading sessions:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل الجلسات',
        });
        this.loading = false;
      },
    });
  }

  revoke(s: SessionDto) {
    if (s.isRevoked) return;

    this.confirmService.confirm({
      message: `هل أنت متأكد من إنهاء هذه الجلسة؟`,
      header: 'تأكيد الإنهاء',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'نعم',
      rejectLabel: 'لا',
      accept: () => {
        this.api.revokeUserSession(this.selectedUserId, s.id).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: 'تم إنهاء الجلسة بنجاح',
            });
            this.load();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'فشل إنهاء الجلسة';
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: errorMsg,
            });
          },
        });
      },
    });
  }

  revokeAll() {
    if (!this.selectedUserId) return;

    this.confirmService.confirm({
      message: `هل أنت متأكد من إنهاء جميع الجلسات النشطة لهذا المستخدم؟`,
      header: 'تأكيد الإنهاء',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'نعم',
      rejectLabel: 'لا',
      accept: () => {
        this.api.revokeAllUserSessionsOld(this.selectedUserId).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: 'تم إنهاء جميع الجلسات بنجاح',
            });
            this.load();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'فشل إنهاء الجلسات';
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: errorMsg,
            });
          },
        });
      },
    });
  }

  clearSelection() {
    this.selectedUserId = '';
    this.selectedUser = null;
    this.rows = [];
    this.userSearch = '';
    this.candidateUsers = [];
  }

  getStatusSeverity(session: SessionDto): string {
    if (session.isRevoked) return 'danger';
    const now = new Date();
    const expiresAt = session.expiresAt ? new Date(session.expiresAt) : null;
    if (expiresAt && expiresAt < now) return 'warning';
    return 'success';
  }

  getStatusLabel(session: SessionDto): string {
    if (session.isRevoked) return 'منتهية';
    const now = new Date();
    const expiresAt = session.expiresAt ? new Date(session.expiresAt) : null;
    if (expiresAt && expiresAt < now) return 'منتهية الصلاحية';
    return 'نشطة';
  }

  isExpired(session: SessionDto): boolean {
    if (session.isRevoked) return false;
    if (!session.expiresAt) return false;
    return new Date(session.expiresAt) < new Date();
  }

  getDeviceIcon(device?: string): string {
    if (!device) return 'pi-desktop';
    const d = device.toLowerCase();
    if (d.includes('mobile') || d.includes('android') || d.includes('ios') || d.includes('iphone')) {
      return 'pi-mobile';
    }
    if (d.includes('tablet') || d.includes('ipad')) {
      return 'pi-tablet';
    }
    return 'pi-desktop';
  }
}
